import { lastValueFrom } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';

import { PORTAL_API_BASE_URL } from '@echospaces-web-monorepo/es-portal-shell';

export type PlaceholderType = 'visible' | 'audible' | 'readable' | 'usable' | 'reactive';
export type PlaceholderStatus = 'active' | 'deprecated' | 'deleted';

export interface Placeholder {
  id: string;
  slug: string;
  slugPath: string | null;
  displayName?: string;
  type: PlaceholderType;
  spaceId: string;
  placeholderStatus: PlaceholderStatus;
  versionId: string | null;
  createdDate: string;
  updatedDate: string;
}

/**
 * Read-only state for placeholders in the current portal session.
 * Authoring (create / update / delete) lives in the EMT (editor surface) —
 * the portal never writes placeholders, only links content INTO them.
 *
 * Backed by `GET /api/ecms/spaces/:spaceId/placeholders` which walks the
 * subtree rooted at the given space (so picking a building shows every
 * placeholder on the building + floors + rooms beneath it).
 */
@Injectable({ providedIn: 'root' })
export class PlaceholderService {
  private readonly http = inject(HttpClient);
  private readonly api = inject(PORTAL_API_BASE_URL);

  private readonly _placeholders = signal<Placeholder[]>([]);
  private readonly _loading = signal(false);

  readonly placeholders = this._placeholders.asReadonly();
  readonly loading = this._loading.asReadonly();

  async loadForSpace(spaceId: string): Promise<void> {
    this._loading.set(true);
    try {
      const data = await lastValueFrom(
        this.http.get<Placeholder[]>(`${this.api}/ecms/spaces/${spaceId}/placeholders`),
      );
      this._placeholders.set(data);
    } finally {
      this._loading.set(false);
    }
  }

  clear(): void {
    this._placeholders.set([]);
  }
}
